// ProfileDetails.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useFormik } from "formik";
import { Alert } from "@material-ui/lab";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import { makeStyles } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { SDCloudBackendAPIWithToken } from "apis/backendAPI";
import { useHistory } from "react-router-dom";
import { SHOW_LOADER, HIDE_LOADER, SHOW_NOTIFICATION, HIDE_NOTIFICATION, SET_NAVBAR_TITLE } from "action_creators/actioncreator";
import { useDispatch } from "react-redux";

// Local
import ProfileTable from "./ProfileTable";
import LoaderHoc from "components/HOC/LoaderHoc";

const inputProps = {
  disableAnimation: true,
  shrink: false,
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "25px 30px 30px 45px",
    border: "1px solid #F0F0F0",
    boxSizing: "border-box",
    background: "#FFFFFF",
    position: "relative",
    marginTop: "15px",
  },
  body2: {
    color: "#838383",
    paddingTop: "10px",
    fontSize: "12px",
  },
  formControl: {
    width: "100%",
    marginTop: "5px",
  },
  buttOutlined: {
    color: theme.palette.buttonColor?.main,
    borderColor: theme.palette.buttonColor?.main,
    "&:hover": {
      border: `1px solid ${theme.palette.buttonColor?.main}`,
    },
  },
}));

const ProfileDetails = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [menuItems, setMenuItems] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [quotaErrors, setQuotaErrors] = useState({});
  const [hoursState, setHoursState] = useState({});
  const [dismissedAlerts, setDismissedAlerts] = useState({});

  // raw server data (no JSX)
  const [learningsRaw, setLearningsRaw] = useState([]);
  const [showcasesRaw, setShowcasesRaw] = useState([]);
  const [sandboxesRaw, setSandboxesRaw] = useState([]);

  const maxHours = history.location?.state?.maxhours ?? 0;

  const columns = [
    {
      Header: "Template Name",
      accessor: "template_name",
    },
    {
      Header: "Name",
      accessor: "display_name",
    },
    // {
    //   Header: "Can View",
    //   accessor: "can_view",
    // },
    {
      Header: "Permission",
      accessor: "permission",
    },
    {
      Header: "Lab Hours",
      accessor: "lab_hours",
    },
    {
      Header: "Allocated Hours",
      accessor: "allocate_hours",
    },
    {
      Header: "Allocate Quota",
      accessor: "quota"
    }
  ];
  const handlePrevious = () => {
    history.push(
      {
        pathname:"/instructer/addtraining",
        state: {
          training_name : history.location.state.training_name,
          training_email: history.location.state.training_email,
          training_prefix: history.location.state.training_prefix,
          time_zone : history.location.state.time_zone,
          start_date: history.location.state.start_date,
          end_date: history.location.state.end_date,
          no_of_participants: history.location.state.no_of_participants,
          description: history.location.state.description,
          endaction:history.location.state.endaction,
          roles: history.location.state.roles,
        },
      });
  };
  useEffect(() => {
      const activeErrors = Object.keys(quotaErrors);
      setDismissedAlerts((prev) => {
        const updated = { ...prev };
        activeErrors.forEach((key) => {
          // If there's a new error, reset dismissed state
          if (quotaErrors[key] && prev[key]) {
            updated[key] = false;
          }
        });
        return updated;
      });
    }, [quotaErrors]);
  const formik = useFormik({
    initialValues: {
      deal_id: "",
      learningPermissions: [],
      learningViews: [],
      sandboxPermissions: [],
      sandboxViews: [],
    },
    onSubmit: async (values) => {
      let hasInvalidHours = false;
      let hasLessThanLabHours = false;
    
      const getLabHours = (name, type) => {
        const data = type === "learning" ? learningsRows : sandboxesRows;
        const item = data.find((d) => d.template_name === name);
        return item ? item.lab_hours : 0;
      };
    
      const validatePermissions = (type, permissionList) => {
        permissionList.forEach((item) => {
          const val = document.getElementById(`${item}_quota`)?.value?.trim();
          const alloc = Number(val);
          const lab = getLabHours(item, type);
    
          if (!val || isNaN(alloc) || alloc <= 0) {
            hasInvalidHours = true;
          } else if (alloc < lab) {
            hasLessThanLabHours = true;
          }
        });
      };
    
      // Run validations
      validatePermissions("learning", values.learningPermissions);
      validatePermissions("sandbox", values.sandboxPermissions);
    
      // Show separate alerts
      if (hasInvalidHours) {
        alert("Please enter valid Quota (greater than 0) for all selected permissions.");
        return;
      }
    
      if (hasLessThanLabHours) {
        alert("Quota must be greater than or equal to Lab Hours.");
        return;
      }
      const findRow = (name, type) => {
        const data = type === "Learning" ? learningsRows : sandboxesRows;
        return data.find(d => d.template_name === name);
      };

      const buildPermissions = (type, views, creates) => {
        const result = [];
    
        views.forEach((item) => {
          if (!creates.includes(item)) {
            result.push({
              resource_type: type,
              resource_name: item,
              permission: "view",
              alloc_hours: 0,
            });
          }
        });
    
        creates.forEach((item) => {
           const row = findRow(item, type);
          result.push({
            resource_type: type,
            resource_name: item,
            permission: "create",
            alloc_hours: row?.allocate_hours || 0,
            quota_value: document.getElementById(`${item}_quota`)?.value || 0
          });
        });
    
        return result;
      };
    
      const permissions = [
        ...buildPermissions("Learning", values.learningViews, values.learningPermissions),
        ...buildPermissions("Sandbox", values.sandboxViews, values.sandboxPermissions),
      ];
    
      const payload = {
        deal_id: values.deal_id,
        training_name : history.location.state.training_name,
        time_zone : history.location.state.time_zone,
        start_date: history.location.state.start_date,
        end_date: history.location.state.end_date,
        upload_file: history.location.state.upload_file,
        no_of_participants: history.location.state.no_of_participants,
        permissions: permissions,
        description: history.location.state.description,
        roles:  history.location.state.roles,
        training_prefix: history.location.state.training_prefix,
      };
      dispatch(SHOW_LOADER());
      SDCloudBackendAPIWithToken()
        .post("/instructer/add_new_training/", payload)
        .then((resp) => resp.data)
        .then((data) => {
          let referenceId = data.context.reference_id;
          let range = history.location.state.no_of_participants;
          for(let i=1; i<= range; i++){
            let prefix = history.location.state.training_prefix;
            let username = prefix + "_" + "user" + String(i) + "_" + String(referenceId);
            let profile_name = String(values.deal_id)+'-'+ String(referenceId) + "_" + "user" + String(i);
            let email = history.location.state.training_email;
            let payload = {
              profile_name: profile_name,
              permissions: permissions,
            };
            SDCloudBackendAPIWithToken()
            .post("/create_profile", payload)
            .then((resp) => resp.data)
            .then((data) => {
              payload = {
                username: username,
                email: email,
                privilege: "developer",
                permission_profile_name: profile_name,
                base_url: `https://${window.location.host}/auth/`,
                endaction:history.location.state.endaction,
                "roles" : history.location.state.roles,
                "is_participant": true,
              };
        
              SDCloudBackendAPIWithToken()
                .post("/create_user", payload)
                .then((resp) => resp.data)
                .then((data) => {
                  let reset_id = data.reset_id;
                  payload = {
                    "username": username,
                    "password": username,
                    "email": email,
                    "access_token": "",
                    "reset_id": reset_id,
                    
                  }
                  SDCloudBackendAPIWithToken()
                  .post("/reset_password", payload)
                  .then((resp) => resp.data)
                  .then((data) => {
                      dispatch(HIDE_LOADER());
                      const payload = {
                        message: "Training Created Successfully.",
                        color: "success",
                      };
                      dispatch(SHOW_NOTIFICATION({ payload }));
                  })
                  .catch((err) => {
                    dispatch(HIDE_LOADER());
                    let message = err.response.data.message;
                    const payload = {
                      message: err?.response?.data?.message || "Password Reset Failed",
                      color: "danger",
                      error: err
                    };
                    payload.message && dispatch(SHOW_NOTIFICATION({ payload }));   
                  });
                  history.push("/instructer-access");
                  setTimeout(() => {
                    dispatch(HIDE_NOTIFICATION()); 
                  }, 2000);
                })
                .catch((err) => {
                  dispatch(HIDE_LOADER());
                  let message = err.response.data.message;
                  const payload = {
                    message: err?.response?.data?.message || "User Creation Failed",
                    color: "danger",
                    error: err
                  };
                  payload.message && dispatch(SHOW_NOTIFICATION({ payload }));   
                });
            })
            .catch((err) => {
              dispatch(HIDE_LOADER());
              let message = err.response.data.message;
              const payload = {
                message: err?.response?.data?.message || "Profile Creation Failed",
                color: "danger",
                error: err
              };
              payload.message && dispatch(SHOW_NOTIFICATION({ payload }));   
            });
          }
        })
        .catch((err) => {
          dispatch(HIDE_LOADER());
          let message = err.response.data.message;
          const payload = {
            message: err?.response?.data?.message || "Unable to create New Training",
            color: "danger",
            error: err
          };
          payload.message && dispatch(SHOW_NOTIFICATION({ payload }));          
        });
    },
    validate: (values) => {
      let errors = {};

      if (!values.deal_id) {
        //console.log("deal id error");
        errors.deal_id = "Required";
      }
      return errors;
    },
  });

  // quota validation logic (memoized)
  const checkQuotaRange = useCallback((value, resourceName, currentAllocated, lab_hours) => {
    const quota = Number(value);
    const allowedHours = maxHours + maxHours / 2;
    let error = "";
    const maxHoursValue = (currentAllocated ?? 0) > maxHours ? maxHours : (currentAllocated ?? 0);

    if (!value && value !== 0 && value !== "0") {
      error = "";
    } else if (value && value.includes(".")) {
      error = `Decimal values are not allowed for ${resourceName}. Please enter a whole number.`;
    } else if (!isNaN(quota) && currentAllocated != null && quota > Number(currentAllocated)) {
      error = `Allocate Quota for ${resourceName} cannot exceed allocated hours ${currentAllocated}.`;
    } else if (lab_hours) {
      const min = Number(lab_hours);
      const max = 3 * Number(lab_hours);
      if (quota < min) {
        error = `Allocate Quota for ${resourceName} should be at least equal to lab hours for Learnings and Showcases`;
      } else if (quota > max) {
        error = `Allocate Quota for ${resourceName} cannot exceed 3 times of lab hours for Learnings and Showcases`;
      }
    } else {
      if (quota <= 0) {
        error = `Allocate Quota for ${resourceName} cannot be less than or equal to 0 for Sandboxes`;
      } else if (quota < maxHoursValue) {
        error = `Allocate Quota for ${resourceName} cannot be less than ${maxHoursValue} for Sandboxes`;
      } else if (quota > allowedHours) {
        error = `Allocate Quota for ${resourceName} cannot be greater than ${allowedHours} for Sandboxes`;
      }
    }

    setQuotaErrors(prev => {
      const newErrors = { ...prev };
      if (error) newErrors[resourceName] = error;
      else delete newErrors[resourceName];
      return newErrors;
    });
  }, [maxHours]);

  // stable handler factory for quota inputs
  const handleQuotaChange = useCallback((resourceName, displayName, allocatedHours, labHours) => (e) => {
      const value = e.target.value;
      const newQuota = value === "" ? "" : Number(value);
      setHoursState(prev => ({ ...prev, [resourceName]: newQuota }));
      if (!isNaN(newQuota) && newQuota > 0) {
        setQuotaErrors(prev => {
          const p = { ...prev };
          delete p[resourceName];
          return p;
        });
      }
  
      checkQuotaRange(value, displayName, allocatedHours, labHours);
    }, [checkQuotaRange]);

  const renderTemplateData = useCallback(async () => {
    try {
      const resp = await SDCloudBackendAPIWithToken().get("/get_resources");
      const data = resp.data || { learnings: [], sandboxes: [] };

      const learnings = [];
      const showcases = [];
      const sandboxes = [];

      (data.learnings || []).forEach((item) => {
        const raw = {
          name: item.name,
          display_name: item.display_name || item.name,
          labhours: item.labhours ?? 0,
          allocated_hours: item.allocated_hours ?? 0,
          consumed_hours: item.consumed_hours ?? 0,
          is_scripted_demo: !!item.is_scripted_demo,
        };
        if (raw.is_scripted_demo) showcases.push(raw);
        else learnings.push(raw);
      });

      (data.sandboxes || []).forEach((item) => {
        const raw = {
          name: item.name,
          display_name: (item.display_name || item.name),
          labhours: item.labhours ?? 0,
          allocated_hours: item.allocated_hours ?? 0,
          designer_objects: item.designer_objects || [],
        };
        if (item.name === "designer_portal") {
          // skip designer portal list in normal sandboxes rows if you want
        } else sandboxes.push(raw);
      });

      setLearningsRaw(learnings);
      setShowcasesRaw(showcases);
      setSandboxesRaw(sandboxes);
    } catch (err) {
      console.error("Failed to fetch resources", err);
    } finally {
      setShowLoader(false);
    }
  }, []);

  // fetch deal ids once
  const renderDealIdDropdown = async () => {
      try {
        let accountName = JSON.parse(localStorage.getItem("profileData"))?.account;
        const resp = await SDCloudBackendAPIWithToken().get(`/get_engagement_ids?account=${accountName}`);
        const data = resp.data;
        let engagements = data.engagements_id_list || [];
        let items = engagements.map((item) => (<option key={uuidv4()} value={item}>{item}</option>));
        items = [<option key={uuidv4()} value={null} selected>None</option>, ...items];
        setMenuItems(items);
      } catch (err) {
        console.error(err);
      }
    };

  useEffect(() => {
    dispatch(SET_NAVBAR_TITLE("New Training"));
    renderDealIdDropdown();
    renderTemplateData();
  }, []);

  // memoized rows (render-ready JSX). hoursState changes will re-run only these memos.
  const learningsRows = useMemo(() => {
    return learningsRaw.map((item) => ({
      template_name: item.name,
      display_name: item.display_name,
      permission: (
        <FormControlLabel
          control={
            <Switch
              value={item.name}
              name="learningPermissions"
              color="default"
              onChange={formik.handleChange}
            />
          }
        />
      ),
      lab_hours: item.labhours,
      allocate_hours: item.allocated_hours || 0,
      quota: (
        <TextField
          type="number"
          variant="filled"
          name={`${item.name}_quota`}
          fullWidth
          style={{ paddingRight: "15px" }}
          onChange={handleQuotaChange(item.name, item.display_name, item.allocated_hours, item.labhours)}
          inputProps={{ id: `${item.name}_quota` }}
          InputLabelProps={inputProps}
          value={hoursState[item.name] ?? ""}
        />
      ),
    }));
  }, [learningsRaw, hoursState, formik, handleQuotaChange]);

  const sandboxesRows = useMemo(() => {
    return sandboxesRaw.map((item) => ({
      template_name: item.name,
      display_name: item.display_name,
      permission: (
        <FormControlLabel
          control={
            <Switch
              value={item.name}
              name="sandboxPermissions"
              color="default"
              onChange={formik.handleChange}
            />
          }
        />
      ),
      lab_hours: item.labhours,
      allocate_hours: item.allocated_hours || 0,
      quota: (
        <TextField
          type="number"
          variant="filled"
          name={`${item.name}_quota`}
          fullWidth
          style={{ paddingRight: "15px" }}
          onChange={handleQuotaChange(item.name, item.display_name, item.allocated_hours)}
          inputProps={{ id: `${item.name}_quota` }}
          InputLabelProps={inputProps}
          value={hoursState[item.name] ?? ((item.allocated_hours ?? 0) > maxHours ? maxHours : (item.allocated_hours ?? 0))}
        />
      ),
    }));
  }, [sandboxesRaw, hoursState, formik, handleQuotaChange]);


  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <Grid container className={classes.container}>
          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="h4">Lab Allocation</Typography>
            <Typography variant="body2" className={classes.body2}>
              Fill all the required details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6}></Grid>
            <FormControl variant="filled" className={classes.formControl}>
              <InputLabel id="demo-simple-select-helper-label">
                Select Deal ID
              </InputLabel>
              <Select
                labelId="demo-simple-select-filled-label"
                id="demo-simple-select-filled"
                labelWidth={100}
                fullWidth
                name="deal_id"
                value={formik.values.deal_id}
                onChange={formik.handleChange}
                native
                error={formik.errors.deal_id ? true : false}
                helperText={formik.errors.deal_id}
              >
                {menuItems.length > 0 ? (
                  menuItems
                ) : (
                  <option value="">None</option>
                )}
              </Select>
            </FormControl>
          {showLoader ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                marginTop: "80px",
              }}
            >
              <LoaderHoc isLoading={showLoader}></LoaderHoc>
            </div>
          ) : (
            <>
              <Grid item xs={12} md={12} style={{ marginTop: "35px" }}>
                <Grid container justify="space-between">
                  <ProfileTable
                    columns={columns}
                    data={learningsRows}
                    tableTitle="Learning Labs"
                    buttonTitle="Create Profile"
                    dropDownValues={[]}
                    hiddenColumns={["template_name"]}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} md={12} style={{ marginTop: "35px" }}>
                <Grid container justify="space-between">
                  <ProfileTable
                    columns={columns}
                    data={sandboxesRows}
                    tableTitle="Sandboxes"
                    buttonTitle="Create Profile"
                    dropDownValues={[]}
                    hiddenColumns={["template_name", "lab_hours"]}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} sm={8} md={8}></Grid>
              <Grid item xs={12} sm={2} md={2}>
                <FormControl fullWidth style={{width:"90%"}}>
                  <Button
                    onClick={handlePrevious}
                    fullWidth
                    variant="contained"
                    color="secondary"
                    disableElevation
                    disableFocusRipple
                    type="submit"
                    
                  >
                    Previous
                  </Button>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2} md={2}>
                <FormControl fullWidth>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    disableElevation
                    disableFocusRipple
                    type="submit"
                    disabled={Object.values(quotaErrors).some(Boolean)}
                  >
                    Create
                  </Button>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12}>
                {Object.entries(quotaErrors).map(([resource, message]) => {
                  if (!message || dismissedAlerts[resource]) return null; // hide if dismissed
                  return (
                    <Alert
                      key={resource}
                      severity="error"
                      onClose={() =>
                        setDismissedAlerts((prev) => ({ ...prev, [resource]: true }))
                      }
                      sx={{ mb: 1 }}
                    >
                      {message}
                    </Alert>
                  );
                })}
              </Grid>
            </>
          )}
          </Grid>
      </form>
    </div>
  );
};

export default ProfileDetails;
